import _ from "lodash";
import { AVATAR_COLORS } from "Constants/colorConstants";
import { hashString } from "Utils";
import { CURRICULUM_TYPE_ORDER_MAPPING } from "Constants";
import {
  CURRICULUM_TYPE_DP,
  COURSE_BASED_GRADING,
} from "Constants/stringConstants";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

const EMPTY_OBJ = {};
const EMPTY_ARRAY = [];

const curriculumFlagsCache = new Map();

export const getCurriculumFlagsForClass = classItem => {
  const curriculumProgram = _.get(classItem, "curriculumProgram", EMPTY_OBJ);
  const { id, type, academicSetupType } = curriculumProgram;

  if (id && curriculumFlagsCache.has(id)) {
    return curriculumFlagsCache.get(id);
  }

  const flags = {
    isDP: type === CURRICULUM_TYPE_DP,
    isCourseBasedGrading: academicSetupType === COURSE_BASED_GRADING,
    isLearningCoursesEnabled: getIsLearningCoursesEnabled({
      curriculumProgramId: id,
    }),
  };

  if (id) {
    curriculumFlagsCache.set(id, flags);
  }

  return flags;
};

const extractAcronymForAvatar = name => {
  const firstWord = name.trim().split(/\s+/)[0];
  return firstWord.substring(0, 2);
};

export const getPrefixElement = classItem => {
  const id = _.get(classItem, "id", "");
  const profileImageData = _.get(classItem, "profileImageData", {});
  const name = _.get(classItem, "title", "");
  let acronym = _.get(profileImageData, "acronym", "");
  if (_.isEmpty(acronym)) {
    acronym = extractAcronymForAvatar(name);
  }

  const color =
    _.get(profileImageData, "color", "") ||
    AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length];

  if (_.isEmpty(profileImageData)) {
    return [
      {
        type: "avatar",
        size: 28,
        name: extractAcronymForAvatar(name),
        color: color,
        id: id,
        theme: "light",
        shape: "square",
        borderRadius: "5",
      },
    ];
  }
  return [
    {
      type: "avatar",
      color: color,
      name: acronym,
      size: 28,
      theme: "light",
      shape: "square",
      borderRadius: "5",
    },
  ];
};

export const getGridHeaders = ({ t, showLevelsColumn }) => {
  const headers = [
    {
      id: "checkbox",
      columnKey: "checkbox",
      width: 36,
      cellType: "checkbox",
      toggleAllCheckboxesOnHeaderClick: false,
      isVisible: true,
    },
    {
      id: "className",
      columnKey: "className",
      title: t("common:class_plural"),
      width: showLevelsColumn ? 420 : 840,
      isVisible: true,
    },
    {
      id: "levelsOpted",
      columnKey: "className",
      title: t("course:levels_opted"),
      width: 420,
      isVisible: showLevelsColumn,
    },
  ];

  return _.filter(headers, "isVisible");
};

export const getGridData = ({
  t,
  classFeed,
  selectedClasses,
  excludedAcademicCourseCodeIds,
}) => {
  return _.map(classFeed, item => {
    const { id, title, learningCourse, subjectLevelMap, genericTags } =
      item || EMPTY_OBJ;

    const {
      isDP,
      isCourseBasedGrading,
      isLearningCoursesEnabled,
    } = getCurriculumFlagsForClass(item);

    const learningCourseTitle = _.get(learningCourse, "0.title", "");
    const currentSelectedClass = _.find(selectedClasses, c => c?.id === id);
    const isCurrentClassSelected = !!currentSelectedClass;

    const currentAcademicCourseCodeId = _.get(
      learningCourse,
      "0.academicCourse.id",
      ""
    );

    const isDuplicateAcademicCourse =
      _.some(
        selectedClasses,
        c =>
          _.get(c, "learningCourse[0].academicCourse.id") ===
          currentAcademicCourseCodeId
      ) ||
      _.includes(excludedAcademicCourseCodeIds, currentAcademicCourseCodeId);

    const isClassSelectionDisabled =
      isDuplicateAcademicCourse && !isCurrentClassSelected;

    const levelsOpted = isCurrentClassSelected
      ? _.get(currentSelectedClass, "levels", EMPTY_ARRAY)
      : EMPTY_ARRAY;

    const releventTagsBasedOnType = isDP
      ? _.filter(genericTags, { type: "SUBJECT_LEVEL" })
      : genericTags;

    const genericTagOptions = _.map(releventTagsBasedOnType, tag => ({
      id: tag.id,
      label: tag.label,
      abbreviation: tag.abbreviation || tag.label,
    }));

    const subjectLevelOptions = getSubjectLevelOptions({
      isDP,
      subjectLevelMap,
    });

    const filteredSubjectLevelOptions = _.filter(
      subjectLevelOptions,
      ({ options }) => _.size(options) > 0
    );

    const flatLevelOptions =
      isCourseBasedGrading || (isDP && !isLearningCoursesEnabled)
        ? genericTagOptions
        : _.flatMap(filteredSubjectLevelOptions, "options");

    const associatedLevelCellType =
      _.size(flatLevelOptions) > 0 ? "dropdown" : "label";

    const hasAssociatedLevels = _.size(flatLevelOptions) > 0;

    const associatedLevelColumn = {
      cellType: associatedLevelCellType,
      isEditable: true,
      placeholder: hasAssociatedLevels ? t("common:select") : t("common:na"),
      ...(hasAssociatedLevels && {
        value: levelsOpted,
        isMulti: !isCourseBasedGrading && !isDP,
        isSelectAllEnabled: false,
        validInputs: {
          options:
            isCourseBasedGrading || (isDP && !isLearningCoursesEnabled)
              ? genericTagOptions
              : filteredSubjectLevelOptions,
        },
        isDisabled: !isCurrentClassSelected,
      }),
    };

    return {
      rowId: id,
      columns: [
        {
          isDisabled: isClassSelectionDisabled,
          cellType: "checkbox",
          value: isCurrentClassSelected ? "checked" : "unchecked",
          isEditable: true,
          tooltip:
            isClassSelectionDisabled &&
            t("schoolSetup:student_already_added_in_other_class_tooltip"),
        },
        {
          cellType: "label",
          value: title,
          prefix: getPrefixElement(item),
          styles: { gap: 8 },
          subtitle: learningCourseTitle,
        },
        associatedLevelColumn,
      ],
    };
  });
};

export const getSubjectLevelOptions = ({ isDP = false, subjectLevelMap }) => {
  const subjectLevelOptions = _.map(
    subjectLevelMap,
    ({ associatedLevels, subject }) => {
      const levelOptions = _.map(associatedLevels, level => ({
        id: isDP ? level?.id : `${subject?.id}-${level?.id}`,
        levelId: level?.id,
        label: level?.abbreviation || level?.label,
        subjectId: subject?.id,
      }));

      return {
        id: subject?.id,
        label: subject?.name,
        options: levelOptions,
      };
    }
  );

  return _.filter(subjectLevelOptions, ({ options }) => _.size(options) > 0);
};

export const getCurriculumWiseFilterOptions = _.memoize(
  ({ items, curriculumPrograms }) => {
    return _.map(curriculumPrograms, curriculum => {
      const associatedItems = _.filter(
        items,
        item => item?.curriculumProgram?.id === curriculum?.id
      );

      return {
        value: curriculum?.id,
        label: curriculum?.label,
        options: _.map(associatedItems, item => ({
          value: item?.id,
          label: item?.name,
        })),
      };
    });
  },
  ({ items, curriculumPrograms }) =>
    `${_.map(items, "id").join("|")}__${_.map(curriculumPrograms, "id").join(
      "|"
    )}`
);

export const styles = {
  modalBody: {
    paddingTop: "0px",
    paddingRight: "0px",
    paddingBottom: "0px",
  },
};

export const getDataGroupedByCurriculum = ({
  data,
  generateSortedData = true,
  fetchCurriculumProgramId = false,
}) => {
  const groupedByCurriculum = _.groupBy(data, item => {
    const {
      curriculumProgram: { id },
    } = item;
    return id;
  });

  if (_.size(groupedByCurriculum) <= 1 && !fetchCurriculumProgramId) {
    return data;
  }

  const formattedData = _.reduce(
    groupedByCurriculum,
    (result, groupedValues) => {
      const acronym = _.get(
        _.first(groupedValues),
        "curriculumProgram.acronym"
      );
      const curriculumProgramType = _.get(
        _.first(groupedValues),
        "curriculumProgram.type"
      );

      result.push({
        label: acronym,
        options: groupedValues,
        curriculumProgramType,
        ...(fetchCurriculumProgramId
          ? {
              curriculumProgramId: _.get(
                _.first(groupedValues),
                "curriculumProgram.id"
              ),
            }
          : {}),
      });

      return result;
    },
    []
  );

  if (!generateSortedData) {
    return formattedData;
  }

  const sortedData = _.orderBy(
    formattedData,
    [obj => CURRICULUM_TYPE_ORDER_MAPPING[obj.curriculumProgramType]],
    "asc"
  );

  return sortedData;
};
